import CAMPAIGN_STATE from './campaignState';

const CAMPAIGN_ACTION = {
  DELETE_DRAFT: 'delete-draft',
  CLONE_BRIEF: 'clone-brief',
  COMPLETE_BRIEF: 'complete-brief',
  REVERT_TO_DRAFT: 'revert-to-draft',
  PAUSE_APPLICATIONS: 'pause-applications',
  RESUME_APPLICATIONS: 'resume-applications',
  CLOSE_CAMPAIGN: 'close-campaign',
} as const;

export type CampaignActionId =
  (typeof CAMPAIGN_ACTION)[keyof typeof CAMPAIGN_ACTION];

// Do not allow cloning of legacy TikTok networks
// https://lightricks.atlassian.net/browse/BRAN-936
const LEGACY_TIKTOK_NETWORKS = ['tiktok', 'tiktok_creator_marketplace'];

export interface ActionsForStateOptions {
  generatedByBriefAi: boolean;
  network?: string | null;
  canClone?: boolean;
  canRevert?: boolean;
  canDelete?: boolean;
  canEdit?: boolean;
  canPauseApplications?: boolean;
  canResumeApplications?: boolean;
  canClose?: boolean;
}

export function actionsForState(
  state: string,
  options: ActionsForStateOptions
): CampaignActionId[] {
  const {
    generatedByBriefAi,
    network,
    canClone = true,
    canRevert = true,
    canDelete = true,
    canEdit = true,
    canPauseApplications = true,
    canResumeApplications = true,
    canClose = true,
  } = options;
  let actions: CampaignActionId[] = [];

  switch (state) {
    case CAMPAIGN_STATE.DRAFT:
    case CAMPAIGN_STATE.UNDER_CONSTRUCTION:
      actions = [
        CAMPAIGN_ACTION.DELETE_DRAFT,
        CAMPAIGN_ACTION.COMPLETE_BRIEF,
        CAMPAIGN_ACTION.CLONE_BRIEF,
      ];
      break;
    case CAMPAIGN_STATE.UNDER_REVIEW:
      actions = [CAMPAIGN_ACTION.REVERT_TO_DRAFT];
      break;
    case CAMPAIGN_STATE.ACCEPTING_PROPOSALS:
      actions = [
        CAMPAIGN_ACTION.PAUSE_APPLICATIONS,
        CAMPAIGN_ACTION.CLONE_BRIEF,
      ];
      break;
    case CAMPAIGN_STATE.CLOSED_TO_NEW_PROPOSALS:
      // Mirrors V1: closing a campaign is only offered once applications have
      // been paused (i.e. from the closed_to_new_proposals state).
      actions = [
        CAMPAIGN_ACTION.RESUME_APPLICATIONS,
        CAMPAIGN_ACTION.CLOSE_CAMPAIGN,
        CAMPAIGN_ACTION.CLONE_BRIEF,
      ];
      break;
    case CAMPAIGN_STATE.APPROVED:
    case CAMPAIGN_STATE.CLOSED:
      actions = [CAMPAIGN_ACTION.CLONE_BRIEF];
      break;
    default:
      break;
  }

  const blocked = new Set<CampaignActionId>();
  if (
    !generatedByBriefAi ||
    !canClone ||
    (network != null && LEGACY_TIKTOK_NETWORKS.includes(network))
  ) {
    blocked.add(CAMPAIGN_ACTION.CLONE_BRIEF);
  }
  if (!canRevert) {
    blocked.add(CAMPAIGN_ACTION.REVERT_TO_DRAFT);
  }
  if (!canDelete) {
    blocked.add(CAMPAIGN_ACTION.DELETE_DRAFT);
  }
  if (!canEdit) {
    blocked.add(CAMPAIGN_ACTION.COMPLETE_BRIEF);
  }
  if (!canPauseApplications) {
    blocked.add(CAMPAIGN_ACTION.PAUSE_APPLICATIONS);
  }
  if (!canResumeApplications) {
    blocked.add(CAMPAIGN_ACTION.RESUME_APPLICATIONS);
  }
  if (!canClose) {
    blocked.add(CAMPAIGN_ACTION.CLOSE_CAMPAIGN);
  }

  return blocked.size
    ? actions.filter((action) => !blocked.has(action))
    : actions;
}

export function hasActionsForState(
  state: string,
  options: ActionsForStateOptions
): boolean {
  return actionsForState(state, options).length > 0;
}

export default CAMPAIGN_ACTION;
